import { motion, MotionConfig, useInView, useMotionValueEvent, useReducedMotion, useScroll } from 'framer-motion';
import type { Variants } from 'framer-motion';
import { ArrowRight, Ban, Check, FileCheck2, FileText, FolderOpen, Handshake, Loader2, Network, Phone, Play, Plus, Presentation, RotateCcw, ScanSearch, Star, Upload, Volume2, X } from 'lucide-react';
import { lazy, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import soniaPoster from '../assets/images/sonia-video-poster.jpg';
import { WhatsAppIcon } from '../components/SocialIcons';
import { WEB_APP_URL } from '../tracker/config';
import { ARROW_BLOCKS, GOLD, JOIN_STEPS, ORANGE } from './p2pArrow';

const P2PArrowScene = lazy(() => import('./P2PArrowScene'));

// ─── PATH TO PLACEMENT (P2P) ───────────────────────────────────────────────────
// For students who finished a course but have no job yet. The form (at the top)
// sends the student, their skills and their resume to the sales CRM
// (server/routes/crm.js webLead, form 'p2p'); a salesperson calls them. Nothing is paid on this page: the
// test fee comes after a meeting with a Lead Manager. The fees come from the
// admin's P2P settings (GET /api/p2p-info), with these as the fallback.

// ─── CONFIG ─────────────────────────────────────────────────────────────────────
const PHONE = '9894447857';
const PHONE_LABEL = '98944 47857';
const WHATSAPP_URL = `https://wa.me/91${PHONE}?text=${encodeURIComponent("Hi Vista Tech, I saw the Path to Placement (P2P) page. I've finished my course but don't have a job yet. Can you tell me more?")}`;
const GOOGLE_REVIEWS_URL = 'https://maps.app.goo.gl/sjz7bY3cbBdHQ1t3A';
const PAGE_URL = 'https://vistatech.org/p2p';
const MAX_RESUME_MB = 5;
// ownyourclick (visits + leads for this page). Loaded here and not in index.html, because its
// form capture would otherwise reach every form on the site, the CRM and tracker included.
// No data-form on purpose: submit() tells it about a lead once the CRM has accepted the form.
const OYC_SRC = 'https://go.ownyourclick.com/oyc.js?p=b9a83643-04fb-406d-9c39-efd6e83f7084';

// The explainer videos sit in Firebase Storage, whose rules refuse a plain link. Each file's
// access token comes from the Firebase console (Storage > the file > its download link).
const VIDEO_TOKEN_TAMIL = 'cff837a3-0121-4709-bc14-31055772bbe9';
const VIDEO_TOKEN_ENGLISH = 'a1171b84-5c9c-47e3-a967-65d1fe0bb214';
const storageVideo = (path: string, token: string) =>
  `https://firebasestorage.googleapis.com/v0/b/vista-tech-website.firebasestorage.app/o/${encodeURIComponent(path)}?alt=media${token ? `&token=${token}` : ''}`;

type Lang = 'ta' | 'en';
const VIDEOS: Record<Lang, { label: string; name: string; src: string }> = {
  ta: { label: 'தமிழ்', name: 'Tamil', src: storageVideo('video/viststech tamilll cc.mp4', VIDEO_TOKEN_TAMIL) },
  en: { label: 'English', name: 'English', src: storageVideo('video/sonia english name card added.mp4', VIDEO_TOKEN_ENGLISH) },
};

// before = testFee + balance (paid before placement); after: only once placed
type Fees = { testFee: number; balance: number; before: number; after: number; total: number };
const DEFAULT_FEES: Fees = { testFee: 1000, balance: 9000, before: 10000, after: 10000, total: 20000 };
const rupees = (n: number) => `₹${n.toLocaleString('en-IN')}`;

const FONT = "'Zilla Slab', Georgia, 'Times New Roman', serif";

// the three sections in the page's earlier look keep its faces and colours
const FONT_BODY = "'Plus Jakarta Sans', Inter, system-ui, -apple-system, sans-serif";
const FONT_SERIF = "'Instrument Serif', Georgia, 'Times New Roman', serif";
const COLOR = { gold: '#F0B429', orange: '#FF6535', violet: '#4e3a69', paper: '#ffffff', mist: '#f7f4fb', ink: '#1c1529', inkSoft: '#5c5270', onDarkSoft: 'rgba(255, 255, 255, 0.74)' };
const container: React.CSSProperties = { maxWidth: 1180, margin: '0 auto', padding: '0 20px' };

// ─── CONTENT ────────────────────────────────────────────────────────────────────
const HERO = {
  title: 'Path to Placement with referrals & network.',
  sub: "You don't need to learn a new course to get placed.",
  cta: 'Apply for P2P',
};

// From Sonia's own slide. Her "6 years" is the figure the slide gives (the admin's choice, 1 Nov 2026).
const SONIA = {
  photo: 'https://res.cloudinary.com/zmdfv4o7/image/upload/c_thumb,g_face,ar_4:5,z_0.55,w_640,f_auto,q_auto/v1793530903/WhatsApp_Image_2026-11-01_at_4.22.08_PM.jpg',
  name: 'Sonia Prabhakaran',
  role: 'Senior Analyst and Career Strategist, Vista Tech',
  intro: 'A professional journey in Data and Business Intelligence, from Germany to Chennai. She explains P2P in the video.',
  years: '6 years',
  yearsNote: 'industry experience in companies',
  quote: 'Your resume gets you in the race. Your portfolio shows why you deserve to win.',
  closing: 'Career growth is built through relevant skills, professional experience, and informed career decisions.',
  careerTitle: 'Career progression through experience and expertise',
  career: [
    { title: 'Data Analyst, Germany', body: 'One year of experience as a Data Analyst at GFOS, Germany.', color: '#4e3a69' },
    { title: 'Competitive Intelligence Analysis, Germany', body: 'Four years of experience in Data and Business Intelligence at Essity.', color: ORANGE },
    { title: 'Senior Analyst and Career Strategist, Vista Tech', body: 'Two years of experience at Vista Tech.', color: GOLD },
  ],
};

// In this order, with the course last and in bold
const PAINS = [
  { have: 'Certificate', missing: 'no referrals?' },
  { have: 'Skills', missing: 'no network?' },
  { have: 'Projects', missing: 'no interview calls?' },
  { have: 'LinkedIn', missing: 'no portfolio?' },
  { have: 'Course completed', missing: 'no job?' },
];

// Around the P2P circle, starting top left and going clockwise on a wide screen
const PILLARS = [
  { Icon: ScanSearch, title: 'Profile-wise plan', body: 'We study your profile and aim you at the roles you can get now.', pos: 'pp-pos-1' },
  { Icon: Handshake, title: 'Referral interviews', body: 'Interviews through referrals, not only job portal applications.', pos: 'pp-pos-2' },
  { Icon: Presentation, title: 'Live analyst mentorship', body: 'Working analysts mentor you live, through every round.', pos: 'pp-pos-3' },
  { Icon: FolderOpen, title: 'Portfolio enhancement', body: 'The projects you already built, shaped into a portfolio recruiters open.', pos: 'pp-pos-4' },
  { Icon: Network, title: 'Network', body: 'Get seen by the people who hire, through our network.', pos: 'pp-pos-5' },
  { Icon: FileCheck2, title: 'Resume & application tracking', body: 'An ATS-ready resume, and every application tracked till the offer.', pos: 'pp-pos-6' },
];

const DONT = [
  { title: 'Fake experience', body: 'We never put made-up experience on your resume. Background checks catch it.' },
  { title: 'Another new course', body: "You've already learned. P2P works with the skills you have now." },
  { title: 'Backdoor jobs', body: 'No paid shortcuts or backdoor offers. Real openings, real interviews.' },
];

// The pricing card: what each part of P2P is worth on its own (the student pays
// the admin's P2P fee, fees.total)
const VALUE_ITEMS = [
  { name: 'Profile management', worth: 10000 },
  { name: 'Application tracker tool', worth: 10000 },
  { name: 'Upskilling consultation', worth: 10000 },
  { name: 'Referral interviews based on portfolio enhancement', worth: 5000 },
  { name: 'Expert meet', worth: 5000 },
  { name: 'Networking support (LinkedIn)', worth: 5000 },
  { name: 'Soft skills', worth: 10000 },
];

const steps = (f: Fees) => [
  { title: 'Apply', body: 'Fill the form at the top of this page with your skills and resume. Nothing to pay here.' },
  { title: 'Talk to our team', body: 'We call you, understand your profile and answer your questions.' },
  { title: 'Meet an HR Manager', body: 'A short online meeting about your profile and your path.' },
  { title: 'Take the test', price: rupees(f.testFee), body: `Didn't pass? You get your ${rupees(f.testFee)} back.` },
  { title: 'Pass and start', price: rupees(f.balance), body: f.after ? `Pay the balance (${rupees(f.before)} in all before placement), and your 6 weeks begin. The other ${rupees(f.after)} only after you're placed.` : 'Pay the balance, and your 6 weeks to placement begin.' },
];

type StoryVideo = { id: string; name: string; role: string; company: string; pay: string; background: string; videoSrc: string; poster: string };

// the same placement videos as /analyst and /mentors
const PLACEMENT_VIDEOS: StoryVideo[] = [
  { id: 'yamini', name: 'Yamini', role: 'Data Analyst', company: 'Citi', pay: '4.9 LPA', background: '2025 passed out', videoSrc: 'https://res.cloudinary.com/zmdfv4o7/video/upload/v1790240837/yamini-1.mp4', poster: 'https://res.cloudinary.com/zmdfv4o7/video/upload/f_auto,q_auto,w_400/v1790240837/yamini-1.jpg' },
  { id: 'jamuna-citi', name: 'Jamuna', role: 'Technical Analyst', company: 'Citi', pay: '4.9 LPA', background: 'Career gap', videoSrc: 'https://res.cloudinary.com/zmdfv4o7/video/upload/v1790240933/yellow_shirt_girl-1.mp4', poster: 'https://res.cloudinary.com/zmdfv4o7/video/upload/f_auto,q_auto,w_400/v1790240933/yellow_shirt_girl-1.jpg' },
  { id: 'vasikaramani', name: 'Vasikaramani.M', role: 'Data Processing Analyst', company: 'Roop Namyang', pay: '4.2 LPA', background: 'Career gap', videoSrc: 'https://res.cloudinary.com/zmdfv4o7/video/upload/v1790241023/WhatsApp_Video_2026-09-24_at_2.38.03_PM.mp4', poster: 'https://res.cloudinary.com/zmdfv4o7/video/upload/f_auto,q_auto,w_400/v1790241023/WhatsApp_Video_2026-09-24_at_2.38.03_PM.jpg' },
  { id: 'monish', name: 'Monish', role: 'Business Analyst', company: 'Ifluids Engineering', pay: '3 LPA', background: 'Fresher', videoSrc: 'https://res.cloudinary.com/zmdfv4o7/video/upload/v1790240956/VID_20260923_133449.mp4', poster: 'https://res.cloudinary.com/zmdfv4o7/video/upload/f_auto,q_auto,w_400/v1790240956/VID_20260923_133449.jpg' },
];

const placedPhoto = (file: string, zoom = 0.62) => `https://res.cloudinary.com/zmdfv4o7/image/upload/c_thumb,g_face,ar_1:1,z_${zoom},w_320,f_auto,q_auto/${file}`;

const PLACED = [
  { name: 'Meghana K.', degree: 'B.Tech IT', role: 'Data Scientist', company: 'Cognizant', pay: '5 LPA', photo: placedPhoto('v1790241677/Meghana-_Data_scientist.jpg', 0.9) },
  { name: 'Vignesh R.', degree: 'B.Tech AI/DS', role: 'Business Analyst', company: 'Prodapt', pay: '4.5 LPA', photo: placedPhoto('v1790241678/vignesh_business_analyst.jpg') },
  { name: 'Rohit Prakash', degree: 'MCA', role: 'Data Analyst', company: 'Kyndryl', pay: '4 LPA', photo: placedPhoto('v1790241678/Rohit_Prakash-Data_anlyst.png') },
  { name: 'Saravanan M', degree: 'B.Tech IT', role: 'Data Analyst', company: 'Kyndryl', pay: '4 LPA', photo: placedPhoto('v1790241678/saravnan_-_data_analyst.jpg') },
  { name: 'Ajay Krishna', degree: 'BCA', role: 'Power BI Developer', company: 'Hansiba Technologies', pay: '3.8 LPA', photo: placedPhoto('v1790241678/Ajay_Krishna_-_powerbi.png') },
  { name: 'Karthik S.', degree: 'B.Sc CSE', role: 'Operations Analyst', company: 'CBTS', pay: '3.8 LPA', photo: placedPhoto('v1790241677/karthik-operation_Analyst.jpg') },
  { name: 'Arjun', degree: 'B.Tech IT', role: 'Junior Data Scientist', company: 'Changbee', pay: '3.6 LPA', photo: placedPhoto('v1790241677/Arjun-Data_Scientist.jpg') },
  { name: 'Vikram N', degree: 'BE CSE', role: 'Power BI Developer', company: 'Zillion Technologies', pay: '3.5 LPA', photo: placedPhoto('v1790241679/Vikram_-_Powerbi.png') },
  { name: 'Rajesh P.', degree: 'BCA', role: 'Operations Analyst', company: 'Contus', pay: '3.5 LPA', photo: placedPhoto('v1790241676/Rajesh-Operation_analyst.jpg') },
];

// copied from the Google reviews already on /mentors, /analyst and the Success Stories page
const REVIEWS = {
  rating: '4.8',
  items: [
    { name: 'Logesh S', rating: 5, text: 'Excellent institute for both learning and placement. Trainers ensure you understand concepts deeply, and the placement support is outstanding.' },
    { name: 'Dr. Arul Mathi', rating: 5, text: "We reached out to Vista Tech for our sister's training. With your guidance she got placed as a Junior Data Analyst within 2 months. Thank you for all your help!" },
    { name: 'Pooja Sivakumar', rating: 5, text: 'I completed my Java Full Stack Development course here, and it was an amazing experience. Thanks to their guidance and placement support, I got placed in a good company. Totally recommend this institute!' },
  ],
};

// also the payment page's form (P2PPay.tsx)
export const DEGREES = ['B.E / B.Tech', 'B.Sc', 'BCA', 'B.Com', 'BBA', 'B.A', 'M.E / M.Tech', 'MCA', 'M.Sc', 'MBA', 'Diploma', 'Other'];
export const YEARS = ['2026', '2025', '2024', '2023', '2022', '2021', '2020', '2019 or before'];

// the quick picks in the form; any other skill can be typed (the server keeps 15, names up to 40 letters)
const SKILL_CHOICES = ['Python', 'SQL', 'Excel', 'Power BI', 'Tableau', 'Java', 'JavaScript', 'React', 'HTML & CSS', 'Node.js', 'Machine Learning', 'Testing'];
const LEVELS = ['Basic', 'Medium', 'Strong'] as const;
const MAX_SKILLS = 15;

// ─── MOTION ─────────────────────────────────────────────────────────────────────
const EASE: [number, number, number, number] = [0.22, 1, 0.36, 1];
const fadeUp: Variants = { hidden: { opacity: 0, y: 24 }, show: { opacity: 1, y: 0, transition: { duration: 0.65, ease: EASE } } };
const stagger: Variants = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };
const liftIn: Variants = { hidden: { y: 22 }, show: { y: 0, transition: { duration: 0.6, ease: EASE } } };
const inView = { initial: 'hidden', whileInView: 'show', viewport: { once: true, margin: '-60px' } } as const;

// ─── SEO + FEES ─────────────────────────────────────────────────────────────────
function useSEO() {
  useEffect(() => {
    window.scrollTo(0, 0);
    const prevTitle = document.title;
    document.title = 'Path to Placement (P2P) · Get placed in 6 weeks | Vista Tech';

    // point the shared tags at this page, and put them back on the way out
    const swaps: Array<[Element, string, string | null]> = [];
    const swap = (selector: string, attr: string, value: string) => {
      const el = document.querySelector(selector);
      if (!el) return;
      swaps.push([el, attr, el.getAttribute(attr)]);
      el.setAttribute(attr, value);
    };
    const description = "Finished a course but no job yet? Path to Placement gets you referral interviews, live analyst mentorship, a portfolio and an ATS-ready resume. No new course. 6 weeks.";
    swap('meta[name="description"]', 'content', description);
    swap('meta[property="og:title"]', 'content', 'Path to Placement (P2P) by Vista Tech: with referrals & network');
    swap('meta[property="og:description"]', 'content', description);
    swap('meta[property="og:url"]', 'content', PAGE_URL);
    swap('link[rel="canonical"]', 'href', PAGE_URL);

    if (!document.getElementById('p2p-fonts')) {
      const link = document.createElement('link');
      link.id = 'p2p-fonts';
      link.rel = 'stylesheet';
      link.href = 'https://fonts.googleapis.com/css2?family=Zilla+Slab:wght@400;500;600;700&family=Noto+Sans+Tamil:wght@700&family=Plus+Jakarta+Sans:wght@400;500;600;700;800&family=Instrument+Serif:ital@0;1&display=swap';
      document.head.appendChild(link);
    }

    if (!document.getElementById('p2p-oyc')) {
      const tag = document.createElement('script');
      tag.id = 'p2p-oyc';
      tag.async = true;
      tag.src = OYC_SRC;
      document.head.appendChild(tag);
    }

    return () => {
      document.title = prevTitle;
      swaps.forEach(([el, attr, value]) => { if (value === null) el.removeAttribute(attr); else el.setAttribute(attr, value); });
    };
  }, []);
}

function useFees() {
  const [fees, setFees] = useState<Fees>(DEFAULT_FEES);
  useEffect(() => {
    let live = true;
    fetch(`${WEB_APP_URL}/p2p-info`)
      .then(r => r.json())
      .then(d => {
        if (!live || !d?.ok || !(d.testFee > 0 && d.balance > 0)) return;
        // a server from before 3 Nov 2026 sends no after-placement fee
        const before = d.testFee + d.balance, after = d.after > 0 ? d.after : 0;
        setFees({ testFee: d.testFee, balance: d.balance, before, after, total: before + after });
      })
      .catch(() => {});
    return () => { live = false; };
  }, []);
  return fees;
}

// ─── SMALL PIECES ───────────────────────────────────────────────────────────────
const scrollToApply = (e: React.MouseEvent) => {
  e.preventDefault();
  document.getElementById('apply')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
};

function ApplyButton({ label = HERO.cta, className = 'pp-btn pp-btn--gold' }: { label?: string; className?: string }) {
  return (
    <a href="#apply" onClick={scrollToApply} className={className}>
      {label} <ArrowRight size={18} aria-hidden="true" />
    </a>
  );
}

// a thin band in the logo's gold: the page's one ornament
function Stripe() {
  return <span aria-hidden="true" className="pp-stripe" />;
}

// The arrow, flat: stands in for the 3D one (no WebGL, data saver, reduced motion) and closes the page
function FlatArrow({ built = JOIN_STEPS, className, quiet = false }: { built?: number; className?: string; quiet?: boolean }) {
  const order = [...ARROW_BLOCKS].sort((a, b) => a.z - b.z);
  return (
    <svg className={className} viewBox="-78 -72 156 148" {...(quiet ? { 'aria-hidden': true } : { role: 'img', 'aria-label': 'The Vista Tech arrow, built from blocks and pointing up' })}>
      {order.map(b => (
        <rect key={`${b.x},${b.y}`} x={b.x - b.w / 2} y={-b.y - b.h / 2} width={b.w} height={b.h} fill={b.color}
          opacity={b.step < built ? 1 : 0.1} transform={b.turn ? `rotate(45 ${b.x} ${-b.y})` : undefined} style={{ transition: 'opacity .35s ease' }} />
      ))}
    </svg>
  );
}

function Eyebrow({ children, onDark = false }: { children: React.ReactNode; onDark?: boolean }) {
  return (
    <p style={{ display: 'inline-flex', alignItems: 'center', gap: 10, margin: 0, fontSize: 12, fontWeight: 700, letterSpacing: '0.18em', textTransform: 'uppercase', color: onDark ? COLOR.gold : COLOR.violet }}>
      <span aria-hidden="true" style={{ width: 22, height: 2, borderRadius: 2, background: onDark ? COLOR.gold : COLOR.orange }} />
      {children}
    </p>
  );
}

// one phrase per heading is set in the serif italic
function Accent({ children, onDark = false, block = false }: { children: React.ReactNode; onDark?: boolean; block?: boolean }) {
  return <span style={{ display: block ? 'block' : undefined, fontFamily: FONT_SERIF, fontStyle: 'italic', fontWeight: 400, fontSize: '1.12em', letterSpacing: '-0.01em', color: onDark ? COLOR.gold : COLOR.violet }}>{children}</span>;
}

function SectionHeading({ eyebrow, heading, accent, sub, onDark = false, accentBlock = false }: { eyebrow: string; heading: string; accent: string; sub?: string; onDark?: boolean; accentBlock?: boolean }) {
  return (
    <motion.div {...inView} variants={fadeUp} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', textAlign: 'center' }}>
      <Eyebrow onDark={onDark}>{eyebrow}</Eyebrow>
      <h2 style={{ margin: '16px 0 0', maxWidth: '22ch', fontSize: 'clamp(30px, 4.4vw, 50px)', fontWeight: 700, letterSpacing: '-0.035em', lineHeight: 1.08, color: onDark ? '#fff' : COLOR.ink }}>
        {heading} <Accent onDark={onDark} block={accentBlock}>{accent}</Accent>
      </h2>
      {sub && <p className="pp-balance" style={{ margin: '16px 0 0', maxWidth: '52ch', fontSize: 'clamp(15.5px, 1.6vw, 17.5px)', lineHeight: 1.65, color: onDark ? COLOR.onDarkSoft : COLOR.inkSoft }}>{sub}</p>}
    </motion.div>
  );
}

function Stars({ rating, size = 15 }: { rating: number; size?: number }) {
  return (
    <span role="img" aria-label={`${rating} out of 5 stars`} style={{ display: 'inline-flex', gap: 2 }}>
      {[1, 2, 3, 4, 5].map(i => <Star key={i} size={size} aria-hidden="true" style={{ color: '#b97a00', fill: i <= rating ? '#F0B429' : 'none' }} />)}
    </span>
  );
}

const GoogleG = ({ size = 28 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true" style={{ flexShrink: 0 }}>
    <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z" />
    <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z" />
    <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z" />
    <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.18 1.48-4.93 2.31-8.16 2.31-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z" />
  </svg>
);

// ─── THE EXPLAINER VIDEO ────────────────────────────────────────────────────────
// Tamil comes first. Browsers only start a video by itself with no sound, so it plays
// silently while on screen, and one tap restarts it from the top with sound. On data saver
// or reduced motion it waits for that tap.
type PlayerState = 'loading' | 'playing' | 'paused' | 'ended' | 'error';

function Explainer() {
  const ref = useRef<HTMLVideoElement>(null);
  const [lang, setLang] = useState<Lang>('ta');
  const [sound, setSound] = useState(false);
  const [state, setState] = useState<PlayerState>('loading');
  const [ratio, setRatio] = useState(9 / 16);
  const stoppedByUs = useRef(false);
  const stoppedByThem = useRef(false);

  useEffect(() => {
    const v = ref.current;
    if (!v) return;
    v.muted = true;
    v.src = VIDEOS.ta.src;
    const saveData = !!(navigator as Navigator & { connection?: { saveData?: boolean } }).connection?.saveData;
    const still = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const seen = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && entry.intersectionRatio >= 0.4) {
        if (!saveData && !still && !stoppedByThem.current && v.paused && !v.ended) v.play().catch(() => setState(s => (s === 'error' ? s : 'paused')));
      } else if (v.muted && !v.paused) {
        stoppedByUs.current = true;
        v.pause();
      }
    }, { threshold: [0, 0.4] });
    seen.observe(v);
    return () => seen.disconnect();
  }, []);

  // all of this runs inside the tap: phones only allow sound from a direct tap
  const playWithSound = (restart: boolean) => {
    const v = ref.current;
    if (!v) return;
    stoppedByThem.current = false;
    v.muted = false;
    setSound(true);
    if (restart) v.currentTime = 0;
    v.play().catch(() => setState(s => (s === 'error' ? s : 'paused')));
  };

  const choose = (next: Lang) => {
    const v = ref.current;
    if (!v || next === lang) return;
    setLang(next);
    setState('loading');
    v.src = VIDEOS[next].src;
    playWithSound(false);
  };

  const retry = () => {
    const v = ref.current;
    if (!v) return;
    setState('loading');
    v.load();
    v.play().catch(() => {});
  };

  const showSoundButton = !sound && state === 'playing';
  const showPlayButton = state === 'paused' && !sound;

  return (
    <div className="pp-explainer">
      <div className="pp-lang" role="group" aria-label="Video language">
        {(['ta', 'en'] as Lang[]).map(l => (
          <button key={l} type="button" lang={l} aria-pressed={lang === l} onClick={() => choose(l)} className={l === 'ta' ? 'pp-lang-ta' : undefined}>
            {VIDEOS[l].label}
          </button>
        ))}
      </div>
      <div className="pp-player" style={{ aspectRatio: String(ratio) }}>
        <video ref={ref} poster={soniaPoster} playsInline preload="metadata" controls={sound} aria-label={`Sonia Prabhakaran explains Path to Placement, in ${VIDEOS[lang].name}`}
          onLoadedMetadata={e => { const v = e.currentTarget; if (v.videoWidth && v.videoHeight) setRatio(v.videoWidth / v.videoHeight); }}
          onPlaying={() => setState('playing')}
          onPause={() => {
            if (!stoppedByUs.current) stoppedByThem.current = true;
            stoppedByUs.current = false;
            setState(s => (s === 'error' || s === 'ended' ? s : 'paused'));
          }}
          onEnded={() => setState('ended')}
          onError={() => setState('error')} />

        {showSoundButton && (
          <button type="button" className="pp-player-btn" onClick={() => playWithSound(true)}>
            <Volume2 size={20} aria-hidden="true" /> Tap for sound
          </button>
        )}
        {showPlayButton && (
          <button type="button" className="pp-player-btn" onClick={() => playWithSound(false)}>
            <Play size={20} fill="currentColor" aria-hidden="true" /> Play with sound
          </button>
        )}
        {state === 'ended' && (
          <button type="button" className="pp-player-btn" onClick={() => playWithSound(true)}>
            <RotateCcw size={20} aria-hidden="true" /> Watch again
          </button>
        )}
        {state === 'error' && (
          <div className="pp-player-error" role="status">
            <p>This video didn't load. Check your internet and try again, or ask us on WhatsApp.</p>
            <div>
              <button type="button" className="pp-btn pp-btn--gold" onClick={retry}><RotateCcw size={17} aria-hidden="true" /> Try again</button>
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="pp-btn pp-btn--light"><WhatsAppIcon size={17} /> WhatsApp</a>
            </div>
          </div>
        )}
      </div>
      <p className="pp-player-caption">
        <a href="#sonia">Sonia Prabhakaran</a> explains P2P. Senior Analyst and Career Strategist.
      </p>
    </div>
  );
}

// ─── HERO ───────────────────────────────────────────────────────────────────────
// The form sits in the hero, so the page opens on it. On a wide screen the video stays beside
// it while the student fills it in; on phones: the title, the video, then the form.
function Hero({ fees }: { fees: Fees }) {
  return (
    <section className="pp-hero">
      <div className="pp-wrap pp-hero-grid">
        <div className="pp-hero-copy">
          <h1>{HERO.title}</h1>
          <p className="pp-hero-sub">{HERO.sub}</p>
          <ul className="pp-hero-trust">
            <li>{rupees(fees.testFee)} test, refunded if you don't pass</li>
            <li>Nothing to pay on this page</li>
          </ul>
        </div>
        <div className="pp-hero-video"><Explainer /></div>
        <div id="apply" className="pp-hero-form">
          <Stripe />
          <ApplyForm />
          <p className="pp-hero-talk">
            <span>Questions first?</span>
            <a href={`tel:${PHONE}`}><Phone size={15} aria-hidden="true" /> {PHONE_LABEL}</a>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer"><WhatsAppIcon size={15} /> WhatsApp</a>
          </p>
        </div>
      </div>
    </section>
  );
}

// ─── SONIA ──────────────────────────────────────────────────────────────────────
function SoniaSection() {
  return (
    <section id="sonia" className="pp-sec pp-sonia">
      <motion.div {...inView} variants={stagger} className="pp-wrap pp-sonia-grid">
        <div className="pp-sonia-head">
          <motion.h2 variants={fadeUp}>Meet {SONIA.name}</motion.h2>
          <motion.p variants={fadeUp} className="pp-sonia-role">{SONIA.role}</motion.p>
          <motion.p variants={fadeUp} className="pp-lead">{SONIA.intro}</motion.p>
        </div>
        <motion.figure variants={fadeUp} className="pp-sonia-photo">
          <img src={SONIA.photo} alt={SONIA.name} loading="lazy" width={640} height={800} />
          <figcaption><strong>{SONIA.years}</strong> {SONIA.yearsNote}</figcaption>
        </motion.figure>
        <div className="pp-sonia-rest">
          <motion.h3 variants={fadeUp}>{SONIA.careerTitle}</motion.h3>
          <motion.ol variants={stagger} className="pp-career">
            {SONIA.career.map(c => (
              <motion.li key={c.title} variants={fadeUp}>
                <span aria-hidden="true" className="pp-career-block" style={{ background: c.color }} />
                <div>
                  <h4>{c.title}</h4>
                  <p>{c.body}</p>
                </div>
              </motion.li>
            ))}
          </motion.ol>
          <motion.blockquote variants={fadeUp} className="pp-quote">“{SONIA.quote}”</motion.blockquote>
          <motion.p variants={fadeUp} className="pp-sonia-closing">{SONIA.closing}</motion.p>
        </div>
      </motion.div>
    </section>
  );
}

// "✓ Certificate — but no referrals?" … the last line (the course) stands out
function PainCard() {
  return (
    <motion.div variants={fadeUp}>
      <div className="pp-pains">
        <p className="pp-pains-title">Sound familiar?</p>
        <ul className="pp-pain-list">
          {PAINS.map((p, i) => {
            const last = i === PAINS.length - 1;
            return (
              <li key={p.have} className={`pp-pain${last ? ' pp-pain--last' : ''}`}>
                <span className="pp-tick" aria-hidden="true"><Check size={last ? 16 : 14} strokeWidth={3} /></span>
                <span className="pp-pain-text">
                  <span className="pp-have">{p.have} <span className="pp-sr">done,</span></span>
                  <span className="pp-miss">but {p.missing}</span>
                </span>
              </li>
            );
          })}
        </ul>
      </div>
    </motion.div>
  );
}


// ─── SOUND FAMILIAR? + 6 WEEKS ──────────────────────────────────────────────────
function SixWeeks() {
  return (
    <section className="pp-pad pp-classic pp-six-section" style={{ background: COLOR.paper }}>
      <motion.div {...inView} variants={stagger} className="pp-six-grid" style={container}>
        <PainCard />
        <div className="pp-six-copy">
          <motion.p variants={fadeUp} className="pp-six">
            <span className="pp-six-num">6 weeks</span> is enough to grab <Accent block>your IT job.</Accent>
          </motion.p>
          <motion.p variants={fadeUp} style={{ margin: '18px 0 0', maxWidth: '46ch', fontSize: 'clamp(15.5px, 1.6vw, 17.5px)', lineHeight: 1.65, color: COLOR.inkSoft }}>
            You already did the learning. P2P is the part between your certificate and your offer letter.
          </motion.p>
          <motion.div variants={fadeUp} style={{ marginTop: 28 }}>
            <ApplyButton label="Start my 6 weeks" className="pp-btn pp-btn--dark" />
          </motion.div>
        </div>
      </motion.div>
    </section>
  );
}

// ─── HOW P2P WORKS ──────────────────────────────────────────────────────────────
function HowItWorks() {
  return (
    <section id="how" className="pp-pad pp-classic" style={{ background: COLOR.mist }}>
      <div style={container}>
        <SectionHeading eyebrow="How it works" heading="How Path to Placement" accent="works." sub="Six things happen around you at the same time, all aimed at one result: your offer letter." />
        <motion.div {...inView} variants={stagger} className="pp-hub-grid">
          {/* on a wide screen, lines from the circle to each card (they run under the cards) */}
          <svg className="pp-hub-lines" viewBox="0 0 100 100" preserveAspectRatio="none" aria-hidden="true">
            {[[17, 17], [83, 17], [83, 50], [83, 83], [17, 83], [17, 50]].map(([x, y]) => (
              <line key={`${x}-${y}`} x1="50" y1="50" x2={x} y2={y} />
            ))}
          </svg>
          <motion.div variants={fadeUp} className="pp-hub-wrap">
            <div className="pp-hub">
              <span aria-hidden="true" className="pp-hub-ring" />
              <span aria-hidden="true" className="pp-hub-ring pp-hub-ring--late" />
              <span className="pp-hub-big">P2P</span>
              <span className="pp-hub-small">Path to Placement</span>
            </div>
          </motion.div>
          {PILLARS.map(({ Icon, title, body, pos }) => (
            <motion.article key={title} variants={fadeUp} className={`pp-pillar ${pos}`}>
              <span className="pp-pillar-icon" aria-hidden="true"><Icon size={22} /></span>
              <div>
                <h3 className="pp-pillar-title">{title}</h3>
                <p className="pp-pillar-body">{body}</p>
              </div>
            </motion.article>
          ))}
        </motion.div>
      </div>
    </section>
  );
}

// ─── WHAT WE DON'T RECOMMEND ────────────────────────────────────────────────────
function DontSection() {
  return (
    <section className="pp-pad pp-classic" style={{ background: COLOR.paper }}>
      <div style={container}>
        <SectionHeading eyebrow="Straight talk" heading="What we" accent="don't recommend." />
        <motion.ul {...inView} variants={stagger} className="pp-dont-grid">
          {DONT.map(d => (
            <motion.li key={d.title} variants={fadeUp} className="pp-dont">
              <span className="pp-dont-icon" aria-hidden="true"><Ban size={22} /></span>
              <h3 className="pp-dont-title"><span className="pp-sr">No </span>{d.title}</h3>
              <p className="pp-dont-body">{d.body}</p>
            </motion.li>
          ))}
        </motion.ul>
        <motion.p {...inView} variants={fadeUp} className="pp-dont-line">
          <span className="pp-dont-badge">P2P</span> Your real skills, real referrals and real interviews. Nothing else.
        </motion.p>
      </div>
    </section>
  );
}

// ─── PRICING ────────────────────────────────────────────────────────────────────
// What each part is worth, the total struck out, the one price, and how little of it comes first
function Pricing({ fees }: { fees: Fees }) {
  const worth = VALUE_ITEMS.reduce((sum, v) => sum + v.worth, 0);
  return (
    <section id="pricing" className="pp-sec pp-pricing">
      <div className="pp-wrap pp-price-grid">
        <motion.div {...inView} variants={stagger} className="pp-price-copy">
          <motion.h2 variants={fadeUp}>Everything in P2P, for one price.</motion.h2>
          <motion.p variants={fadeUp} className="pp-lead">What each part of Path to Placement is worth on its own.</motion.p>
          <motion.div variants={fadeUp} className="pp-split" aria-hidden="true">
            <span className="pp-split-test" style={{ flexBasis: `${Math.max(6, (fees.testFee / fees.total) * 100)}%` }} />
            <span className="pp-split-balance" />
            {fees.after > 0 && <span className="pp-split-after" style={{ flexBasis: `${(fees.after / fees.total) * 100}%` }} />}
          </motion.div>
          <motion.p variants={fadeUp} className="pp-split-text">
            {rupees(fees.testFee)} for the test + {rupees(fees.balance)} after you pass{fees.after > 0 && <> = {rupees(fees.before)} before placement</>}
          </motion.p>
          {fees.after > 0 && <motion.p variants={fadeUp} className="pp-split-later">The other {rupees(fees.after)} only after you're placed.</motion.p>}
          <motion.p variants={fadeUp} className="pp-refund">Didn't pass the test? You get your {rupees(fees.testFee)} back.</motion.p>
          <motion.div variants={fadeUp}><ApplyButton /></motion.div>
        </motion.div>
        <motion.div {...inView} variants={fadeUp} className="pp-slip-wrap">
          <div className="pp-menu">
            <ul>
              {VALUE_ITEMS.map(v => (
                <li key={v.name}>
                  <span className="pp-menu-name">{v.name}</span>
                  <span aria-hidden="true" className="pp-dots" />
                  <span className="pp-menu-worth">{rupees(v.worth)}</span>
                </li>
              ))}
            </ul>
            <p className="pp-menu-total"><span>Total value</span> <s>{rupees(worth)}</s></p>
            <p className="pp-menu-you"><span>Your price</span> <strong>{rupees(fees.total)}</strong></p>
            {fees.after > 0 && (
              <dl className="pp-menu-when">
                <div><dt>Before placement</dt><dd>{rupees(fees.before)}</dd></div>
                <div><dt>After placement</dt><dd>{rupees(fees.after)}</dd></div>
              </dl>
            )}
          </div>
          <span aria-hidden="true" className="pp-zig" />
        </motion.div>
      </div>
    </section>
  );
}

// ─── HOW TO JOIN: THE ARROW BUILDS ITSELF ───────────────────────────────────────
function JoinSection({ fees }: { fees: Fees }) {
  const section = useRef<HTMLElement>(null);
  const list = useRef<HTMLOListElement>(null);
  const near = useInView(section, { once: true, margin: '500px' });
  const still = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: list, offset: ['start 66%', 'end 86%'] });
  const [active, setActive] = useState(0);
  const [failed, setFailed] = useState(false);
  const onFail = useCallback(() => setFailed(true), []);

  useMotionValueEvent(scrollYProgress, 'change', p => setActive(Math.min(JOIN_STEPS - 1, Math.floor(p * JOIN_STEPS))));

  // three.js only loads on a wide screen; phones keep their data and get the small arrows
  const conn = (navigator as Navigator & { connection?: { saveData?: boolean }; deviceMemory?: number });
  const wide = typeof window.matchMedia === 'function' && window.matchMedia('(min-width: 1000px)').matches;
  const lowEnd = !!conn.connection?.saveData || (conn.deviceMemory !== undefined && conn.deviceMemory <= 2);
  const use3D = wide && near && !still && !failed && !lowEnd;
  const flat = <FlatArrow built={still ? JOIN_STEPS : active + 1} className="pp-flat-arrow" />;

  return (
    <section ref={section} id="join" className="pp-sec pp-join">
      <div className="pp-wrap">
        <motion.h2 {...inView} variants={fadeUp}>Five steps from here to your first offer.</motion.h2>
        <div className="pp-join-grid">
          <div className="pp-join-stage">
            {use3D ? <Suspense fallback={flat}><P2PArrowScene progress={scrollYProgress} onFail={onFail} /></Suspense> : flat}
          </div>
          <ol ref={list} className="pp-steps">
            {steps(fees).map((s, i) => (
              <motion.li key={s.title} {...inView} variants={fadeUp} className={still || i <= active ? 'is-on' : undefined}>
                <FlatArrow built={i + 1} quiet className="pp-step-arrow" />
                <div>
                  <h3>{s.title}{s.price && <span> {s.price}</span>}</h3>
                  <p>{s.body}</p>
                </div>
              </motion.li>
            ))}
          </ol>
        </div>
      </div>
    </section>
  );
}

// ─── TESTIMONIALS ───────────────────────────────────────────────────────────────
function VideoCard({ v, active, onPlay, onStop }: { v: StoryVideo; active: boolean; onPlay: () => void; onStop: () => void }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  // mount the player and start it inside the tap itself: phones only allow sound from a direct tap
  const start = () => {
    flushSync(onPlay);
    videoRef.current?.play().catch(() => {});
  };
  return (
    <article className="pp-video-card">
      <div className="pp-video-frame">
        {active ? (
          <video ref={videoRef} src={v.videoSrc} poster={v.poster} controls playsInline onEnded={onStop}
            style={{ display: 'block', width: '100%', height: '100%', objectFit: 'contain', background: '#000' }} />
        ) : (
          <button type="button" onClick={start} className="pp-video-poster" aria-label={`Play ${v.name}'s video, placed at ${v.pay}`}>
            <img src={v.poster} alt="" loading="lazy" style={{ display: 'block', width: '100%', height: '100%', objectFit: 'cover' }} />
            <span aria-hidden="true" className="pp-video-play"><Play size={22} fill="currentColor" style={{ marginLeft: 3 }} /></span>
          </button>
        )}
      </div>
      <h3>{v.name} <span>{v.pay}</span></h3>
      <p>{v.role}, <strong>{v.company}</strong></p>
      <p className="pp-video-bg">{v.background}</p>
    </article>
  );
}

function Testimonials() {
  const [playing, setPlaying] = useState<string | null>(null);
  return (
    <section id="testimonials" className="pp-sec pp-stories">
      <div className="pp-wrap">
        <motion.div {...inView} variants={stagger} className="pp-stories-head">
          <motion.h2 variants={liftIn}>Students we've already placed</motion.h2>
          <motion.p variants={liftIn} className="pp-lead">Real Vista Tech students, in their own words.</motion.p>
        </motion.div>
        <motion.div {...inView} variants={stagger} className="pp-video-row">
          {PLACEMENT_VIDEOS.map(v => (
            <motion.div key={v.id} variants={liftIn}>
              <VideoCard v={v} active={playing === v.id} onPlay={() => setPlaying(v.id)} onStop={() => setPlaying(null)} />
            </motion.div>
          ))}
        </motion.div>

        <motion.h3 {...inView} variants={liftIn} className="pp-placed-title">Placed at Citi, Cognizant, Kyndryl, Prodapt and more</motion.h3>
        <motion.ul {...inView} variants={stagger} className="pp-placed">
          {PLACED.map(p => (
            <motion.li key={p.name} variants={liftIn}>
              <img src={p.photo} alt={p.name} loading="lazy" width={320} height={320} />
              <div>
                <h4>{p.name} <span>{p.pay}</span></h4>
                <p>{p.role}, {p.degree}</p>
                <p>Placed at <strong>{p.company}</strong></p>
              </div>
            </motion.li>
          ))}
        </motion.ul>

        <motion.div {...inView} variants={liftIn} className="pp-letter">
          <a href={GOOGLE_REVIEWS_URL} target="_blank" rel="noopener noreferrer" className="pp-rating">
            <GoogleG />
            <strong>{REVIEWS.rating}</strong>
            <Stars rating={5} />
            <span>Read all reviews on Google</span>
          </a>
          <div className="pp-review-grid">
            {REVIEWS.items.map(r => (
              <figure key={r.name}>
                <blockquote>“{r.text}”</blockquote>
                <figcaption>{r.name} <Stars rating={r.rating} size={14} /></figcaption>
              </figure>
            ))}
          </div>
        </motion.div>
      </div>
    </section>
  );
}

// ─── THE FORM ───────────────────────────────────────────────────────────────────
type Fields = { name: string; phone: string; email: string; degree: string; year: string };
type Level = typeof LEVELS[number];
type Skill = { name: string; level: Level | '' };
type FieldKey = keyof Fields | 'skills' | 'resume';
type Errors = Partial<Record<FieldKey, string>>;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const FIELD_ORDER: FieldKey[] = ['name', 'phone', 'email', 'degree', 'year', 'skills', 'resume'];

// +91 98944 47857, 098944 47857 and 9894447857 are the same number
function mobile10(raw: string) {
  let d = raw.replace(/\D/g, '');
  if (d.length === 12 && d.startsWith('91')) d = d.slice(2);
  else if (d.length === 11 && d.startsWith('0')) d = d.slice(1);
  return /^[6-9]\d{9}$/.test(d) ? d : '';
}

function resumeProblem(file: File) {
  if (!/\.(pdf|docx?)$/i.test(file.name)) return 'Please add your resume as a PDF or Word file (.pdf, .doc, .docx)';
  if (file.size > MAX_RESUME_MB * 1024 * 1024) return `Your resume is too big. Please add a file under ${MAX_RESUME_MB} MB.`;
  if (file.size < 100) return 'That file is empty. Please add your resume again.';
  return '';
}

function check(f: Fields, skills: Skill[], file: File | null): Errors {
  const e: Errors = {};
  if (f.name.trim().length < 2) e.name = 'Please enter your name';
  if (!mobile10(f.phone)) e.phone = 'Please enter your 10-digit mobile number';
  if (!EMAIL_RE.test(f.email.trim())) e.email = 'Please enter a valid email address';
  if (!f.degree) e.degree = 'Please pick your degree';
  if (!f.year) e.year = 'Please pick the year you passed out';
  const noLevel = skills.find(s => !s.level);
  if (!skills.length) e.skills = 'Please add at least one skill';
  else if (noLevel) e.skills = `How good are you at ${noLevel.name}? Pick Basic, Medium or Strong.`;
  if (!file) e.resume = 'Please add your resume';
  return e;
}

const readAsDataUrl = (file: File) => new Promise<string>((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(String(r.result));
  r.onerror = () => reject(new Error("Couldn't read your resume file. Please add it again."));
  r.readAsDataURL(file);
});

const sizeLabel = (bytes: number) => (bytes >= 1024 * 1024 ? `${(bytes / 1024 / 1024).toFixed(1)} MB` : `${Math.max(1, Math.round(bytes / 1024))} KB`);

function Field({ id, label, error, children }: { id: string; label: string; error?: string; children: React.ReactNode }) {
  return (
    <div className="pp-field">
      <label htmlFor={id} className="pp-label">{label}</label>
      {children}
      {error && <p id={`${id}-error`} className="pp-error">{error}</p>}
    </div>
  );
}

// "Python · Strong", "Excel · Medium": tap the quick picks or type a skill, then a level for each
function SkillsPicker({ skills, onChange, error }: { skills: Skill[]; onChange: (next: Skill[]) => void; error?: string }) {
  const [other, setOther] = useState('');
  const full = skills.length >= MAX_SKILLS;
  const find = (name: string) => skills.find(s => s.name.toLowerCase() === name.toLowerCase());

  const add = (raw: string) => {
    const name = raw.replace(/\s+/g, ' ').trim().slice(0, 40);
    if (!name || find(name) || full) return;
    const pick = SKILL_CHOICES.find(c => c.toLowerCase() === name.toLowerCase());
    onChange([...skills, { name: pick || name, level: '' }]);
  };
  const remove = (name: string) => onChange(skills.filter(s => s.name !== name));
  const setLevel = (name: string, level: Level) => onChange(skills.map(s => (s.name === name ? { ...s, level } : s)));
  const addOther = () => { add(other); setOther(''); };

  return (
    <fieldset className="pp-skills" aria-describedby={error ? 'p2p-skills-error' : undefined}>
      <legend className="pp-label">Your skills</legend>
      <p className="pp-hint">Tap the skills you have, then pick how good you are at each.</p>
      <div className="pp-skill-pick">
      <div className="pp-chips">
        {SKILL_CHOICES.map((c, i) => {
          const on = !!find(c);
          return (
            <button key={c} id={i === 0 ? 'p2p-skills' : undefined} type="button" className="pp-chip" aria-pressed={on}
              disabled={!on && full} onClick={() => (on ? remove(c) : add(c))}>
              {on ? <Check size={15} strokeWidth={3} aria-hidden="true" /> : <Plus size={15} aria-hidden="true" />}{c}
            </button>
          );
        })}
      </div>
      <div className="pp-skill-add">
        <label htmlFor="p2p-other-skill" className="pp-sr">Another skill</label>
        <input id="p2p-other-skill" className="pp-input" maxLength={40} placeholder="Another skill?" value={other} disabled={full}
          onChange={e => setOther(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addOther(); } }} />
        <button type="button" className="pp-add-btn" onClick={addOther} disabled={full || !other.trim()}>Add</button>
      </div>
      </div>
      {skills.length > 0 && (
        <ul className="pp-skill-list">
          {skills.map((s, i) => (
            <li key={s.name} className={`pp-skill-row${error && !s.level ? ' is-bad' : ''}`}>
              <span id={`p2p-skill-name-${i}`} className="pp-skill-name">{s.name}</span>
              <span className="pp-levels" role="radiogroup" aria-labelledby={`p2p-skill-name-${i}`}>
                {LEVELS.map((l, j) => (
                  <label key={l} className="pp-level">
                    <input type="radio" id={j === 0 ? `p2p-level-${i}` : undefined} name={`p2p-level-${s.name}`} value={l}
                      checked={s.level === l} onChange={() => setLevel(s.name, l)} />
                    <span>{l}</span>
                  </label>
                ))}
              </span>
              <button type="button" className="pp-skill-x" onClick={() => remove(s.name)} aria-label={`Remove ${s.name}`}><X size={17} /></button>
            </li>
          ))}
        </ul>
      )}
      {error && <p id="p2p-skills-error" className="pp-error">{error}</p>}
    </fieldset>
  );
}

function ApplyForm() {
  const [f, setF] = useState<Fields>({ name: '', phone: '', email: '', degree: '', year: '' });
  const [skills, setSkills] = useState<Skill[]>([]);
  const [file, setFile] = useState<File | null>(null);
  const [trap, setTrap] = useState('');
  const [errors, setErrors] = useState<Errors>({});
  const [sending, setSending] = useState(false);
  const [fail, setFail] = useState('');
  const [doneName, setDoneName] = useState('');
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const doneRef = useRef<HTMLHeadingElement>(null);

  useEffect(() => { if (doneName) doneRef.current?.focus(); }, [doneName]);

  const set = (k: keyof Fields) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const value = e.target.value;
    setF(p => ({ ...p, [k]: value }));
    if (errors[k]) setErrors(p => ({ ...p, [k]: undefined }));
  };

  const changeSkills = (next: Skill[]) => {
    setSkills(next);
    if (errors.skills) setErrors(p => ({ ...p, skills: undefined }));
  };

  const pick = (list: FileList | null) => {
    const one = list && list[0];
    if (!one) return;
    const problem = resumeProblem(one);
    setErrors(p => ({ ...p, resume: problem || undefined }));
    setFile(problem ? null : one);
    if (fileRef.current) fileRef.current.value = '';
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (sending) return;
    const errs = check(f, skills, file);
    setErrors(errs);
    setFail('');
    const first = FIELD_ORDER.find(k => errs[k]);
    if (first) {
      // a skill with no level: its first level button, else the field itself
      const noLevel = first === 'skills' ? skills.findIndex(s => !s.level) : -1;
      document.getElementById(noLevel >= 0 ? `p2p-level-${noLevel}` : `p2p-${first}`)?.focus();
      return;
    }
    setSending(true);
    try {
      const data = await readAsDataUrl(file!);
      let res: Response;
      try {
        res = await fetch(`${WEB_APP_URL}/web-lead`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            form: 'p2p', name: f.name.trim(), phone: mobile10(f.phone), email: f.email.trim(),
            degree: f.degree, passedOutYear: f.year, skills: skills.map(s => ({ name: s.name, level: s.level })),
            resume: { data, fileName: file!.name }, website: trap,
          }),
        });
      } catch {
        throw new Error("Couldn't send it. Check your internet and try again.");
      }
      const out = await res.json().catch(() => null);
      if (!out || !out.ok) throw new Error((out && out.error) || 'Something went wrong. Please try again, or call us.');
      (window as any).oyc?.('submit', {
        name: f.name.trim(), phone: mobile10(f.phone), email: f.email.trim(), degree: f.degree, year: f.year,
        skills: skills.map(sk => `${sk.name} (${sk.level})`).join(', '),
      }, 'P2P form');
      setDoneName(f.name.trim().split(/\s+/)[0]);
    } catch (err) {
      setFail(err instanceof Error ? err.message : 'Something went wrong. Please try again, or call us.');
    } finally {
      setSending(false);
    }
  };

  if (doneName) {
    return (
      <div className="pp-form pp-done" role="status">
        <span className="pp-done-icon" aria-hidden="true"><Check size={30} strokeWidth={3} /></span>
        <h3 ref={doneRef} tabIndex={-1} style={{ margin: '18px 0 0', fontSize: 26, fontWeight: 800, letterSpacing: '-0.03em', color: '#1c1529', outline: 'none' }}>
          Thank you, {doneName}!
        </h3>
        <p style={{ margin: '10px 0 0', fontSize: 16, lineHeight: 1.6, color: '#51476a' }}>
          We have your details and your resume. Our team will call you on <strong style={{ color: '#1c1529', whiteSpace: 'nowrap' }}>{mobile10(f.phone).replace(/^(\d{5})/, '$1 ')}</strong> soon. There's nothing to pay yet.
        </p>
        <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="pp-btn pp-btn--gold" style={{ marginTop: 24 }}>
          <WhatsAppIcon size={19} /> Chat with us on WhatsApp
        </a>
      </div>
    );
  }

  const aria = (k: FieldKey) => (errors[k] ? { 'aria-invalid': true, 'aria-describedby': `p2p-${k}-error` } : {});

  return (
    <form className="pp-form" onSubmit={submit} noValidate>
      <div className="pp-form-head">
        <h2 className="pp-form-title">Apply for P2P</h2>
        <p className="pp-form-sub">Two minutes. Keep your resume ready.</p>
      </div>

      {/* bots fill this; people never see it */}
      <div aria-hidden="true" className="pp-trap">
        <label htmlFor="p2p-website">Website</label>
        <input id="p2p-website" name="website" tabIndex={-1} autoComplete="off" value={trap} onChange={e => setTrap(e.target.value)} />
      </div>

      <div className="pp-fields">
        <div className="pp-f-name">
          <Field id="p2p-name" label="Full name" error={errors.name}>
            <input id="p2p-name" className="pp-input" autoComplete="name" maxLength={80} value={f.name} onChange={set('name')} {...aria('name')} />
          </Field>
        </div>
        <div className="pp-f-half">
          <Field id="p2p-phone" label="Mobile number" error={errors.phone}>
            <input id="p2p-phone" className="pp-input" type="tel" inputMode="tel" autoComplete="tel" maxLength={16} placeholder="10-digit number" value={f.phone} onChange={set('phone')} {...aria('phone')} />
          </Field>
        </div>
        <div className="pp-f-half">
          <Field id="p2p-email" label="Email" error={errors.email}>
            <input id="p2p-email" className="pp-input" type="email" inputMode="email" autoComplete="email" maxLength={120} value={f.email} onChange={set('email')} {...aria('email')} />
          </Field>
        </div>
        <div className="pp-f-pick">
          <Field id="p2p-degree" label="Degree" error={errors.degree}>
            <select id="p2p-degree" className="pp-input pp-select" value={f.degree} onChange={set('degree')} {...aria('degree')}>
              <option value="">Pick one</option>
              {DEGREES.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </Field>
        </div>
        <div className="pp-f-pick">
          <Field id="p2p-year" label="Passed out in" error={errors.year}>
            <select id="p2p-year" className="pp-input pp-select" value={f.year} onChange={set('year')} {...aria('year')}>
              <option value="">Pick a year</option>
              {YEARS.map(y => <option key={y} value={y}>{y}</option>)}
            </select>
          </Field>
        </div>
      </div>

      <SkillsPicker skills={skills} onChange={changeSkills} error={errors.skills} />

      <div className="pp-form-end">
      <div className="pp-field">
        <span className="pp-label" id="p2p-resume-label">Resume</span>
        <input ref={fileRef} id="p2p-resume" type="file" className="pp-file"
          accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
          aria-labelledby="p2p-resume-label" onChange={e => pick(e.target.files)} {...aria('resume')} />
        {file ? (
          <div className="pp-file-chosen">
            <span className="pp-file-icon" aria-hidden="true"><FileText size={20} /></span>
            <span style={{ minWidth: 0, flex: 1 }}>
              <span className="pp-file-name">{file.name}</span>
              <span className="pp-file-size">{sizeLabel(file.size)}</span>
            </span>
            <button type="button" className="pp-file-x" onClick={() => setFile(null)} aria-label="Remove this resume"><X size={18} /></button>
          </div>
        ) : (
          <label htmlFor="p2p-resume" className={`pp-drop${dragging ? ' is-over' : ''}${errors.resume ? ' is-bad' : ''}`}
            onDragOver={e => { e.preventDefault(); setDragging(true); }}
            onDragLeave={() => setDragging(false)}
            onDrop={e => { e.preventDefault(); setDragging(false); pick(e.dataTransfer.files); }}>
            <Upload size={22} aria-hidden="true" />
            <span><strong>Add your resume</strong> <span className="pp-drop-hint">PDF or Word, up to {MAX_RESUME_MB} MB</span></span>
          </label>
        )}
        {errors.resume && <p id="p2p-resume-error" className="pp-error">{errors.resume}</p>}
      </div>

      <div className="pp-form-go">
        {fail && <p className="pp-fail" role="alert">{fail}</p>}

        <button type="submit" className="pp-btn pp-btn--gold pp-btn--block" disabled={sending}>
          {sending ? <><Loader2 size={18} className="pp-spin" aria-hidden="true" /> Sending…</> : <>Apply for P2P <ArrowRight size={18} aria-hidden="true" /></>}
        </button>
        <p className="pp-consent">Nothing to pay here. By applying, you agree that our team can call or WhatsApp you about P2P.</p>
      </div>
      </div>
    </form>
  );
}


// ─── CLOSING ───────────────────────────────────────────────────────────
function ClosingSection({ fees }: { fees: Fees }) {
  return (
    <section className="pp-sec pp-closing">
      <div className="pp-wrap">
        <motion.div {...inView} variants={stagger} className="pp-reply">
          <div className="pp-reply-copy">
            <motion.h2 variants={liftIn}>Your course is done. Your job is next.</motion.h2>
            <motion.div variants={liftIn} className="pp-closing-ctas">
              <ApplyButton />
              <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" className="pp-btn pp-btn--ghost"><WhatsAppIcon size={18} /> WhatsApp us</a>
            </motion.div>
            <motion.ul variants={liftIn} className="pp-trust">
              {['Nothing to pay on this page', `${rupees(fees.testFee)} test, refunded if you don't pass`, '6 weeks to placement'].map(t => (
                <li key={t}><Check size={18} strokeWidth={3} aria-hidden="true" /> {t}</li>
              ))}
            </motion.ul>
          </div>
          <motion.div variants={liftIn} className="pp-reply-arrow"><FlatArrow /></motion.div>
        </motion.div>
      </div>
    </section>
  );
}

// phones only: Apply + Call pinned to the bottom once the hero has scrolled
// away, and hidden again while the form is on screen
function MobileCtaBar() {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const onScroll = () => {
      const apply = document.getElementById('apply')?.getBoundingClientRect();
      const atForm = !!apply && apply.top < window.innerHeight && apply.bottom > 0;
      setShow(window.scrollY > 560 && !atForm);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  return (
    <div className={`pp-cta-bar${show ? ' is-on' : ''}`} aria-hidden={!show}>
      <a href="#apply" onClick={scrollToApply} className="pp-btn pp-btn--gold" tabIndex={show ? 0 : -1}>Apply for P2P <ArrowRight size={17} aria-hidden="true" /></a>
      <a href={`tel:${PHONE}`} className="pp-btn pp-btn--light" tabIndex={show ? 0 : -1}><Phone size={17} aria-hidden="true" /> Call</a>
    </div>
  );
}

// ─── GLOBAL STYLES ──────────────────────────────────────────────────────────────
// index.css pads every <section> by 60px on phones with !important, so the section
// padding here is scoped under .pp-page (higher specificity) to win back control.
const GLOBAL_STYLE = `
.pp-page { --plum: #2b2240; --violet: #4e3a69; --night: #130d1d; --gold: #F0B429; --orange: #FF6535; --paper: #ffffff; --mist: #f5f5f5; --ink: #1c1529; --soft: #51476a; --m: clamp(16px, 2.4vw, 36px); --g-dark: linear-gradient(155deg, #1d1530 0%, #2b2240 55%, #3b2d57 100%); --g-block: linear-gradient(120deg, #241b38 0%, #2b2240 45%, #4e3a69 100%); --g-gold: linear-gradient(135deg, #F7C94F 0%, #EFAF23 100%); --g-light: linear-gradient(180deg, #ffffff 0%, #f6f4fa 100%); --g-mist: linear-gradient(180deg, #f6f4fa 0%, #efecf5 100%); position: relative; background: var(--paper); color: var(--ink); font-family: ${FONT}; font-size: clamp(18px, 1.05rem + .25vw, 21px); line-height: 1.5; min-height: 100svh; overflow-x: clip; }
.pp-page ::selection { background: var(--gold); color: var(--ink); }
.pp-page ~ .Toastify { padding: 0 !important; }
.pp-page .pp-sec { padding-top: 72px !important; padding-bottom: 80px !important; }
.pp-page .pp-hero { padding-top: 20px !important; padding-bottom: 72px !important; }
@media (min-width: 1000px) { .pp-page .pp-sec { padding-top: 136px !important; padding-bottom: 144px !important; } .pp-page .pp-hero { padding-top: 24px !important; padding-bottom: 96px !important; } }
.pp-wrap { max-width: 1640px; margin-inline: auto; padding-inline: var(--m); }
.pp-page h1, .pp-page h2, .pp-page h3 { margin: 0; font-family: inherit; font-weight: 600; letter-spacing: -0.025em; line-height: 1.02; text-wrap: balance; }
.pp-page h2 { font-size: clamp(2rem, 1.3rem + 3.2vw, 4rem); }
.pp-page h3 { font-size: clamp(1.375rem, 1.2rem + .7vw, 1.75rem); line-height: 1.12; letter-spacing: -0.015em; }
.pp-page p { margin: 0; }
.pp-lead { font-size: clamp(1.25rem, 1.1rem + .7vw, 1.625rem); line-height: 1.32; }
.pp-sr { position: absolute; width: 1px; height: 1px; padding: 0; margin: -1px; overflow: hidden; clip: rect(0,0,0,0); white-space: nowrap; border: 0; }

.pp-btn { display: inline-flex; align-items: center; justify-content: center; gap: 9px; min-height: 54px; padding: 0 26px; border: 0; border-radius: 0; font-family: inherit; font-size: 18px; font-weight: 600; line-height: 1; text-decoration: none; white-space: nowrap; cursor: pointer; transition: transform .18s ease, background-color .18s ease, color .18s ease; }
.pp-btn:hover { transform: translateY(-2px); }
.pp-btn:active { transform: translateY(1px); }
.pp-btn svg { transition: transform .18s ease; }
.pp-btn:hover svg { transform: translateX(3px); }
.pp-btn:focus-visible, .pp-page a:focus-visible, .pp-lang button:focus-visible, .pp-player-btn:focus-visible { outline: 3px solid var(--orange); outline-offset: 3px; }
.pp-btn:disabled { cursor: wait; opacity: .8; transform: none; }
.pp-btn--gold { background: var(--gold); color: var(--ink); }
.pp-btn--gold:hover { filter: brightness(1.06); }
.pp-btn--ghost { background: var(--paper); color: var(--ink); }
.pp-btn--ghost:hover { background: var(--mist); }
.pp-btn--light { background: var(--paper); color: var(--ink); }
.pp-btn--dark { background: var(--plum); color: #fff; }
.pp-btn--dark:hover { filter: brightness(1.15); }
.pp-btn--block { width: 100%; }
@keyframes pp-spin { to { transform: rotate(360deg); } }
.pp-spin { animation: pp-spin .9s linear infinite; }

.pp-stripe { display: block; height: 6px; background: var(--gold); }


.pp-hero { background: var(--g-dark); color: #fff; }
.pp-hero-grid { display: grid; grid-template-columns: minmax(0, 1fr); gap: 22px; }
.pp-page .pp-hero-copy h1 { font-size: clamp(2.02rem, .9rem + 4.6vw, 4.5rem); letter-spacing: -0.03em; line-height: 1; }
.pp-hero-sub { margin-top: 16px !important; font-size: clamp(1.25rem, 1.1rem + .7vw, 1.625rem); line-height: 1.3; font-weight: 500; }
.pp-hero-trust { list-style: none; margin: 16px 0 0; padding: 0; display: flex; flex-wrap: wrap; gap: 6px 26px; font-size: 18px; color: var(--gold); font-weight: 500; }
.pp-hero-form { min-width: 0; scroll-margin-top: 14px; }
.pp-hero-talk { display: flex; flex-wrap: wrap; align-items: center; gap: 8px 18px; margin-top: 18px !important; font-size: 18px; }
.pp-hero-talk a { display: inline-flex; align-items: center; gap: 7px; color: #fff; font-weight: 600; text-decoration: underline; text-decoration-thickness: 2px; text-decoration-color: var(--gold); text-underline-offset: 4px; }
.pp-explainer { display: flex; flex-direction: column; align-items: center; }
.pp-lang { display: grid; grid-template-columns: 1fr 1fr; width: 100%; max-width: calc(68svh * 9 / 16); }
.pp-lang button { min-height: 50px; border: 0; border-radius: 0; background: var(--violet); color: #fff; font-family: inherit; font-size: 18px; font-weight: 600; cursor: pointer; transition: background-color .2s ease, color .2s ease; }
.pp-lang button:hover { background: #5e4780; }
.pp-lang button[aria-pressed="true"] { background: var(--gold); color: var(--ink); }
.pp-lang-ta { font-family: 'Noto Sans Tamil', 'Nirmala UI', 'Latha', sans-serif !important; }
.pp-player { position: relative; width: 100%; max-width: calc(68svh * 9 / 16); background: var(--night); overflow: hidden; }
.pp-player video { display: block; width: 100%; height: 100%; object-fit: contain; background: var(--night); }
.pp-player-btn { position: absolute; left: 50%; top: 12px; transform: translateX(-50%); display: inline-flex; align-items: center; gap: 9px; min-height: 54px; padding: 0 22px; border: 0; border-radius: 0; background: var(--gold); color: var(--ink); font-family: inherit; font-size: 18px; font-weight: 600; white-space: nowrap; cursor: pointer; transition: background-color .18s ease, transform .18s ease; }
.pp-player-btn:hover { filter: brightness(1.06); transform: translateX(-50%) translateY(-2px); }
.pp-player-error { position: absolute; inset: auto 0 0 0; padding: 18px; background: var(--night); color: #fff; font-size: 18px; line-height: 1.35; }
.pp-player-error > div { display: flex; flex-wrap: wrap; gap: 8px; margin-top: 12px; }
.pp-player-error .pp-btn { flex: 1 1 130px; min-height: 48px; padding: 0 12px; }
.pp-player-caption { margin-top: 10px !important; max-width: calc(68svh * 9 / 16); font-size: 18px; line-height: 1.35; text-align: center; }
.pp-player-caption a { color: #fff; font-weight: 600; text-decoration: underline; text-decoration-thickness: 2px; text-decoration-color: var(--gold); text-underline-offset: 3px; }
@media (min-width: 1000px) {
  .pp-hero-grid { grid-template-columns: auto minmax(0, 1fr); grid-template-areas: "video copy" "video form"; grid-template-rows: auto 1fr; gap: 28px clamp(28px, 3.2vw, 52px); }
  .pp-hero-copy { grid-area: copy; }
  .pp-hero-video { grid-area: video; align-self: start; position: sticky; top: 14px; }
  .pp-hero-form { grid-area: form; }
  .pp-lang, .pp-player, .pp-player-caption { width: calc(min(100svh - 150px, 820px) * 9 / 16); max-width: none; }
}

.pp-sonia { background: var(--g-light); }
.pp-sonia-grid { display: grid; grid-template-columns: minmax(0, 1fr); gap: 34px; }
.pp-sonia-photo { position: relative; margin: 0; }
.pp-sonia-photo img { filter: saturate(.92) contrast(1.02); display: block; width: 100%; height: auto; aspect-ratio: 4 / 5; object-fit: cover; }
.pp-sonia-photo figcaption { display: flex; align-items: baseline; flex-wrap: wrap; gap: 4px 12px; padding: 18px 20px; background: var(--g-block); color: #fff; font-size: 18px; font-weight: 500; line-height: 1.2; }
.pp-sonia-photo strong { color: var(--gold); font-size: clamp(2rem, 1.3rem + 3.2vw, 4rem); font-weight: 600; letter-spacing: -0.025em; line-height: 1; }
.pp-sonia-role { margin-top: 12px !important; font-weight: 600; color: var(--violet); }
.pp-sonia-head .pp-lead { margin-top: 20px !important; max-width: 34em; }
.pp-page .pp-sonia-rest h4 { margin: 0; font-size: inherit; font-weight: 600; line-height: 1.2; }
.pp-career { list-style: none; margin: 24px 0 0; padding: 0; display: grid; gap: 22px; }
.pp-career li { display: grid; grid-template-columns: 26px minmax(0, 1fr); gap: 20px; }
.pp-career-block { width: 20px; height: 20px; margin: 6px 0 0 3px; transform: rotate(45deg); }
.pp-career p { margin-top: 4px !important; }
.pp-quote { margin: 40px 0 0; padding: 26px 28px; background: var(--g-block); color: var(--gold); font-size: clamp(1.45rem, 1.2rem + 1.1vw, 2.25rem); font-weight: 600; line-height: 1.12; letter-spacing: -0.015em; }
.pp-sonia-closing { margin-top: 18px !important; max-width: 36em; }
@media (min-width: 700px) {
  .pp-sonia-grid { grid-template-columns: minmax(0, 5fr) minmax(0, 7fr); grid-template-areas: "photo head" "photo rest"; grid-template-rows: auto 1fr; gap: 36px clamp(40px, 6vw, 96px); align-items: start; }
  .pp-sonia-head { grid-area: head; }
  .pp-sonia-photo { grid-area: photo; }
  .pp-sonia-rest { grid-area: rest; }
}

/* Sound familiar, How it works, What we don't recommend: the page's earlier look */
.pp-classic { font-family: ${FONT_BODY}; font-size: 16px; }
.pp-page .pp-pad { padding-top: 68px !important; padding-bottom: 72px !important; }
@media (min-width: 900px) { .pp-page .pp-pad { padding-top: 104px !important; padding-bottom: 108px !important; } }
.pp-page .pp-six-section { padding-bottom: 76px !important; }
.pp-classic h2, .pp-classic .pp-six, .pp-classic .pp-balance { text-wrap: balance; }
.pp-page .pp-classic .pp-btn { min-height: 52px; padding: 0 24px; border-radius: 999px; font-size: 15.5px; font-weight: 700; }
.pp-page .pp-classic .pp-btn--dark { background: #2b2240; box-shadow: 0 14px 30px -14px rgba(43,34,64,.7); }
.pp-page .pp-classic .pp-btn--dark:hover { background: #4e3a69; filter: none; }

.pp-page .pp-pains { position: relative; width: 100%; max-width: 520px; margin: 0 auto; padding: 26px 22px 22px; border-radius: 26px; background: #fff; color: #1c1529; border: 1px solid rgba(43,34,64,.1); box-shadow: 0 40px 80px -46px rgba(29,20,48,.5); }
@media (min-width: 960px) { .pp-page .pp-pains { margin: 0; padding: 32px 30px 28px; transform: rotate(-1.2deg); } }
.pp-page .pp-pains-title { margin: 0 0 14px; font-family: ${FONT_SERIF}; font-style: italic; font-size: 30px; line-height: 1.1; color: #4e3a69; }
.pp-page .pp-pain-list { list-style: none; margin: 0; padding: 0; display: grid; gap: 4px; }
.pp-page .pp-pain { display: flex; align-items: flex-start; gap: 12px; padding: 11px 4px; border-bottom: 1px dashed rgba(43,34,64,.14); }
.pp-page .pp-tick { flex: 0 0 auto; display: grid; place-items: center; width: 24px; height: 24px; margin-top: 1px; border-radius: 50%; background: #e3f4ea; color: #1e7a4f; }
.pp-page .pp-pain-text { display: flex; flex-wrap: wrap; align-items: baseline; gap: 2px 8px; font-size: 16.5px; line-height: 1.45; }
.pp-page .pp-have { font-weight: 700; }
.pp-page .pp-miss { color: #8d84a1; }
.pp-page .pp-pain--last { margin-top: 10px; padding: 16px 14px; border: 2px solid #F0B429; border-radius: 18px; background: #fff5db; }
.pp-page .pp-pain--last .pp-tick { width: 30px; height: 30px; background: #1e7a4f; color: #fff; }
.pp-page .pp-pain--last .pp-pain-text { font-size: 21px; font-weight: 800; letter-spacing: -0.02em; }
.pp-page .pp-pain--last .pp-have { font-weight: 800; }
.pp-page .pp-pain--last .pp-miss { color: #FF6535; font-weight: 800; }

.pp-page .pp-six-grid { display: grid; grid-template-columns: minmax(0, 1fr); gap: 48px; }
.pp-page .pp-six-copy { display: flex; flex-direction: column; align-items: center; text-align: center; }
.pp-page .pp-six { margin: 0; max-width: 18ch; font-size: clamp(36px, 6vw, 72px); font-weight: 800; letter-spacing: -0.045em; line-height: 1.04; color: #1c1529; }
.pp-page .pp-six-num { background: linear-gradient(100deg, #C8860F 0%, #F0B429 45%, #FF6535 100%); -webkit-background-clip: text; background-clip: text; color: transparent; }
@media (min-width: 960px) {
  .pp-page .pp-six-grid { grid-template-columns: minmax(0, .9fr) minmax(0, 1.1fr); align-items: center; gap: 72px; }
  .pp-page .pp-six-copy { align-items: flex-start; text-align: left; }
  .pp-page .pp-six { font-size: clamp(42px, 4.8vw, 62px); }
}

.pp-page .pp-hub-grid { position: relative; display: grid; grid-template-columns: minmax(0, 1fr); gap: 14px; margin-top: 44px; }
.pp-page .pp-hub-lines { display: none; }
.pp-page .pp-hub-wrap { display: flex; justify-content: center; padding: 10px 0 18px; }
.pp-page .pp-hub { position: relative; display: flex; flex-direction: column; align-items: center; justify-content: center; width: 172px; height: 172px; border-radius: 50%; background: linear-gradient(150deg, #2b2240, #4e3a69); color: #fff; box-shadow: 0 0 0 6px #fff, 0 0 0 8px #F0B429, 0 30px 60px -24px rgba(29,20,48,.7); }
.pp-page .pp-hub-big { font-size: 52px; font-weight: 800; letter-spacing: -0.05em; line-height: 1; color: #F0B429; }
.pp-page .pp-hub-small { margin-top: 6px; font-size: 12px; font-weight: 700; letter-spacing: .08em; text-transform: uppercase; color: rgba(255,255,255,.82); }
.pp-page .pp-hub-ring { position: absolute; inset: -8px; border-radius: 50%; border: 2px solid rgba(240,180,41,.55); opacity: 0; pointer-events: none; }
@keyframes pp-ring { 0% { transform: scale(1); opacity: .7; } 100% { transform: scale(1.45); opacity: 0; } }
@media (prefers-reduced-motion: no-preference) {
  .pp-page .pp-hub-ring { animation: pp-ring 3.2s ease-out infinite; }
  .pp-page .pp-hub-ring--late { animation-delay: 1.6s; }
}
.pp-page .pp-pillar { position: relative; z-index: 1; display: flex; align-items: flex-start; gap: 14px; padding: 20px 18px; border-radius: 20px; background: #fff; border: 1px solid rgba(43,34,64,.1); box-shadow: 0 18px 40px -32px rgba(29,20,48,.45); }
.pp-page .pp-pillar-icon { flex: 0 0 auto; display: grid; place-items: center; width: 46px; height: 46px; border-radius: 14px; background: #fff5db; color: #8a5a00; }
.pp-page .pp-pillar-title { margin: 0; font-size: 17px; font-weight: 700; letter-spacing: -0.02em; line-height: 1.3; color: #1c1529; }
.pp-page .pp-pillar-body { margin: 6px 0 0; font-size: 14.5px; line-height: 1.6; color: #5c5270; }
@media (min-width: 620px) and (max-width: 959px) {
  .pp-page .pp-hub-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .pp-page .pp-hub-wrap { grid-column: 1 / -1; }
}
@media (min-width: 960px) {
  .pp-page .pp-hub-grid { grid-template-columns: minmax(0, 1fr) 250px minmax(0, 1fr); grid-template-rows: repeat(3, auto); gap: 26px 48px; margin-top: 60px; }
  .pp-page .pp-hub-lines { display: block; position: absolute; inset: 0; width: 100%; height: 100%; z-index: 0; overflow: visible; }
  .pp-page .pp-hub-lines line { stroke: #F0B429; stroke-width: 2; stroke-dasharray: 5 6; vector-effect: non-scaling-stroke; }
  .pp-page .pp-hub-wrap { grid-column: 2; grid-row: 1 / span 3; align-self: center; padding: 0; position: relative; z-index: 1; }
  .pp-page .pp-hub { width: 214px; height: 214px; }
  .pp-page .pp-hub-big { font-size: 64px; }
  .pp-page .pp-pos-1 { grid-column: 1; grid-row: 1; }
  .pp-page .pp-pos-6 { grid-column: 1; grid-row: 2; }
  .pp-page .pp-pos-5 { grid-column: 1; grid-row: 3; }
  .pp-page .pp-pos-2 { grid-column: 3; grid-row: 1; }
  .pp-page .pp-pos-3 { grid-column: 3; grid-row: 2; }
  .pp-page .pp-pos-4 { grid-column: 3; grid-row: 3; }
  .pp-page .pp-pos-1, .pp-page .pp-pos-6, .pp-page .pp-pos-5 { flex-direction: row-reverse; text-align: right; }
}

.pp-page .pp-dont-grid { list-style: none; margin: 40px 0 0; padding: 0; display: grid; grid-template-columns: minmax(0, 1fr); gap: 14px; }
.pp-page .pp-dont { position: relative; padding: 24px 22px; border-radius: 22px; background: #fff; border: 1px solid rgba(43,34,64,.1); box-shadow: 0 18px 40px -32px rgba(29,20,48,.4); }
.pp-page .pp-dont-icon { display: grid; place-items: center; width: 46px; height: 46px; border-radius: 14px; background: #ffe9e2; color: #d94b1e; }
.pp-page .pp-dont-title { margin: 16px 0 0; font-size: 20px; font-weight: 800; letter-spacing: -0.02em; color: #1c1529; text-decoration: line-through; text-decoration-color: rgba(255,101,53,.75); text-decoration-thickness: 2px; }
.pp-page .pp-dont-body { margin: 8px 0 0; font-size: 15px; line-height: 1.6; color: #5c5270; }
@media (min-width: 760px) { .pp-page .pp-dont-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 18px; margin-top: 52px; } }
.pp-page .pp-dont-line { display: flex; align-items: center; justify-content: center; flex-wrap: wrap; gap: 10px; margin: 34px 0 0; text-align: center; font-size: 16px; font-weight: 600; color: #1c1529; }
.pp-page .pp-dont-badge { display: inline-flex; padding: 6px 12px; border-radius: 999px; background: #2b2240; color: #F0B429; font-size: 13px; font-weight: 800; letter-spacing: .04em; }

.pp-slip-wrap { min-width: 0; }
.pp-zig { display: block; height: 14px; background: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='24' height='14'%3E%3Cpolygon points='0,0 24,0 12,14' fill='%23ffffff'/%3E%3C/svg%3E") repeat-x left top; }

.pp-pricing { background: var(--g-dark); color: #fff; }
.pp-price-grid { display: grid; grid-template-columns: minmax(0, 1fr); gap: 40px; }
.pp-price-copy .pp-lead { margin-top: 20px !important; max-width: 24em; }
.pp-split { display: flex; height: 44px; margin-top: 36px; gap: 3px; }
.pp-split-test { flex-grow: 0; flex-shrink: 0; background: var(--gold); }
.pp-split-balance { flex: 1; background: #6a5389; }
/* after placement: hatched, paid later */
.pp-split-after { flex-grow: 0; flex-shrink: 0; background: repeating-linear-gradient(135deg, rgba(255, 255, 255, .18) 0 6px, transparent 6px 12px); box-shadow: inset 0 0 0 2px rgba(255, 255, 255, .3); }
.pp-split-text { margin-top: 12px !important; font-weight: 600; font-size: clamp(1.25rem, 1.1rem + .7vw, 1.625rem); line-height: 1.25; }
.pp-split-later { margin-top: 8px !important; font-weight: 600; font-size: clamp(1.05rem, 1rem + .35vw, 1.25rem); color: rgba(255, 255, 255, .78); }
.pp-refund { margin: 10px 0 26px !important; }
.pp-menu { padding: clamp(24px, 3.4vw, 52px); background: var(--paper); color: var(--ink); }
.pp-menu ul { list-style: none; margin: 0; padding: 0; display: grid; gap: 15px; }
.pp-menu li { display: flex; align-items: baseline; gap: 10px; line-height: 1.25; }
.pp-menu-name { flex: 0 1 auto; font-weight: 500; }
.pp-dots { flex: 1 1 18px; min-width: 18px; border-bottom: 4px dotted #8a7fa3; transform: translateY(-4px); }
.pp-menu-worth { flex: 0 0 auto; font-weight: 600; font-variant-numeric: tabular-nums; }
.pp-menu-total { display: flex; justify-content: space-between; align-items: baseline; gap: 12px; margin-top: 26px !important; font-weight: 600; }
.pp-menu-total s { font-size: clamp(1.25rem, 1.1rem + .7vw, 1.625rem); text-decoration-color: var(--orange); text-decoration-thickness: 3px; color: var(--soft); font-variant-numeric: tabular-nums; }
.pp-menu-you { display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 6px 16px; margin-top: 14px !important; padding: 18px 20px; background: var(--g-block); color: #fff; font-weight: 600; }
.pp-menu-you strong { font-size: clamp(2.6rem, 1.7rem + 3.2vw, 4.5rem); line-height: 1; font-weight: 600; letter-spacing: -0.03em; color: var(--gold); }
.pp-menu-when { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 3px; margin: 3px 0 0; }
.pp-menu-when div { padding: 12px 20px 14px; background: var(--g-block); color: #fff; }
.pp-menu-when dt { font-size: 14px; font-weight: 500; color: rgba(255, 255, 255, .74); }
.pp-menu-when dd { margin: 2px 0 0; font-size: clamp(1.35rem, 1.2rem + .6vw, 1.75rem); font-weight: 600; line-height: 1.1; color: var(--gold); font-variant-numeric: tabular-nums; }
@media (min-width: 1000px) { .pp-price-grid { grid-template-columns: minmax(0, 5fr) minmax(0, 6fr); gap: clamp(40px, 6vw, 96px); align-items: center; } }

.pp-join { background: var(--g-mist); }
.pp-join-grid { display: grid; grid-template-columns: minmax(0, 1fr); margin-top: 28px; }
.pp-join-stage { display: none; }
.pp-flat-arrow { display: block; width: 100%; height: 100%; padding: 8px; }
.pp-steps { list-style: none; margin: 0; padding: 0; display: grid; gap: 26px; }
.pp-steps li { display: grid; grid-template-columns: 76px minmax(0, 1fr); gap: 18px; align-items: center; }
.pp-step-arrow { display: block; width: 100%; }
.pp-page .pp-steps h3 { font-size: clamp(1.625rem, 1.3rem + 1.7vw, 3.25rem); letter-spacing: -0.025em; line-height: 1.05; }
.pp-steps h3 span { white-space: nowrap; color: var(--violet); }
.pp-steps p { margin-top: 6px !important; max-width: 26em; }
@media (min-width: 1000px) {
  .pp-join-grid { grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: clamp(40px, 6vw, 96px); margin-top: 0; }
  .pp-join-stage { display: block; position: sticky; top: 13vh; height: 74vh; align-self: start; }
  .pp-join-stage > * { height: 100%; }
  .pp-steps { gap: 0; }
  .pp-steps li { display: flex; flex-direction: column; justify-content: center; align-items: flex-start; min-height: 42vh; color: var(--soft); transition: color .3s ease; }
  .pp-steps li.is-on { color: var(--ink); }
  .pp-step-arrow { display: none; }
}

.pp-stories { background: var(--g-dark); color: #fff; }
.pp-stories-head .pp-lead { margin-top: 16px !important; }
.pp-video-row { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 28px 14px; margin-top: 36px; }
.pp-video-row > div { min-width: 0; }
@media (min-width: 900px) { .pp-video-row { grid-template-columns: repeat(4, minmax(0, 1fr)); margin-top: 56px; gap: 20px; } }
.pp-video-card h3 { margin-top: 14px; }
.pp-video-card h3 { overflow-wrap: anywhere; }
.pp-video-card h3 span { color: var(--gold); white-space: nowrap; }
.pp-video-card p { margin-top: 4px !important; font-size: 18px; line-height: 1.3; }
.pp-video-card strong { font-weight: 600; }
.pp-page .pp-video-bg { color: var(--gold); }
.pp-video-frame { position: relative; aspect-ratio: 9 / 16; overflow: hidden; background: var(--night); }
.pp-video-poster { all: unset; position: absolute; inset: 0; display: block; cursor: pointer; }
.pp-video-poster img { transition: transform .5s cubic-bezier(.22,1,.36,1); }
.pp-video-poster:hover img { transform: scale(1.04); }
.pp-video-poster:focus-visible { outline: 3px solid var(--gold); outline-offset: -3px; }
.pp-video-play { position: absolute; left: 14px; bottom: 14px; width: 58px; height: 58px; display: grid; place-items: center; background: var(--gold); color: var(--ink); transition: transform .2s ease; }
.pp-video-poster:hover .pp-video-play { transform: translateY(-3px); }

.pp-page .pp-placed-title { margin-top: 64px; font-size: clamp(1.625rem, 1.3rem + 1.7vw, 3.25rem); letter-spacing: -0.025em; line-height: 1.05; }
.pp-placed { list-style: none; margin: 24px 0 0; padding: 0; display: grid; grid-template-columns: minmax(0, 1fr); gap: 6px; }
.pp-placed li { display: grid; grid-template-columns: 112px minmax(0, 1fr); background: var(--paper); color: var(--ink); }
.pp-placed img { display: block; width: 100%; height: 100%; min-height: 112px; object-fit: cover; filter: saturate(.88) contrast(1.03); }
.pp-placed li > div { min-width: 0; padding: 12px 16px; }
.pp-page .pp-placed h4 { margin: 0 0 4px; font-size: clamp(1.25rem, 1.1rem + .7vw, 1.625rem); font-weight: 600; letter-spacing: -0.015em; line-height: 1.12; }
.pp-placed h4 span { white-space: nowrap; color: var(--violet); }
.pp-placed p { font-size: 18px; line-height: 1.3; }
.pp-placed strong { font-weight: 600; }
@media (min-width: 700px) { .pp-placed { grid-template-columns: repeat(2, minmax(0, 1fr)); } .pp-placed li:last-child { grid-column: 1 / -1; } }
@media (min-width: 1000px) { .pp-placed { grid-template-columns: repeat(3, minmax(0, 1fr)); } .pp-placed li { grid-template-columns: 148px minmax(0, 1fr); } .pp-placed li:last-child { grid-column: auto; } .pp-placed img { min-height: 148px; } .pp-placed li > div { padding: 16px 18px; align-self: center; } .pp-page .pp-placed-title { margin-top: 88px; } }

.pp-letter { margin-top: 56px; padding: clamp(24px, 3.4vw, 52px); background: var(--paper); color: var(--ink); }
.pp-rating { display: inline-flex; flex-wrap: wrap; align-items: center; gap: 8px 12px; color: var(--ink); text-decoration: none; }
.pp-rating strong { font-size: clamp(2rem, 1.3rem + 3.2vw, 4rem); font-weight: 600; letter-spacing: -0.025em; line-height: 1; }
.pp-rating > span:last-child { font-size: 18px; font-weight: 500; text-decoration: underline; text-decoration-thickness: 2px; text-decoration-color: var(--violet); text-underline-offset: 4px; }
.pp-review-grid { display: grid; grid-template-columns: minmax(0, 1fr); gap: 30px; margin-top: 30px; }
.pp-review-grid figure { margin: 0; display: flex; flex-direction: column; gap: 10px; }
.pp-review-grid blockquote { margin: 0; }
.pp-review-grid figcaption { display: flex; flex-wrap: wrap; align-items: center; gap: 4px 10px; margin-top: auto; font-weight: 600; }
@media (min-width: 900px) { .pp-review-grid { grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 44px; margin-top: 40px; } }

/* the form lays itself out by its own width (it is narrower in the hero than on a tablet) */
.pp-form { container: ppform / inline-size; padding: 26px 20px 22px; border-radius: 0; background: #fff; color: #1c1529; border: 0;  }
@media (min-width: 600px) { .pp-form { padding: 34px 32px 28px; } }
.pp-page .pp-form-title { margin: 0; font-size: clamp(2rem, 1.3rem + 3.2vw, 3rem); color: #1c1529; }
.pp-form-sub { margin: 0 0 4px !important; font-size: 18px; color: #51476a; }
.pp-form-head { display: flex; flex-wrap: wrap; align-items: baseline; gap: 2px 18px; }
.pp-fields { display: grid; grid-template-columns: repeat(6, minmax(0, 1fr)); gap: 0 14px; }
.pp-fields > div { grid-column: span 6; min-width: 0; }
@container ppform (min-width: 400px) { .pp-f-half, .pp-f-pick { grid-column: span 3 !important; } }
@container ppform (min-width: 720px) {
  .pp-f-name, .pp-f-half { grid-column: span 2 !important; }
  .pp-skill-pick { display: flex; flex-wrap: wrap; gap: 8px; }
  .pp-skill-pick .pp-chips { display: contents; }
  .pp-skill-pick .pp-skill-add { flex: 1 1 230px; margin-top: 0; }
  .pp-skill-pick .pp-skill-add .pp-input { min-height: 40px; }
  .pp-skill-pick .pp-add-btn { min-height: 40px; }
  .pp-form-end { display: grid; grid-template-columns: minmax(0, 1fr) minmax(0, 1fr); gap: 14px; align-items: end; }
  .pp-form-go .pp-btn--block { margin-top: 14px !important; }
}
.pp-field { margin-top: 14px; min-width: 0; }
.pp-label { display: block; margin-bottom: 7px; font-size: 14px; font-weight: 700; color: #1c1529; }
.pp-input { display: block; width: 100%; min-height: 50px; padding: 0 16px; border-radius: 0; border: 1.5px solid rgba(43,34,64,.18); background: #fff; font-family: inherit; font-size: 16px; color: #1c1529; transition: border-color .15s ease, box-shadow .15s ease; }
.pp-input:focus { outline: 3px solid #FF6535; outline-offset: 1px; border-color: #4e3a69; }
.pp-input[aria-invalid="true"] { border-color: #d94b1e; }
.pp-select { appearance: none; -webkit-appearance: none; padding-right: 40px; background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 24 24' fill='none' stroke='%235c5270' stroke-width='2.2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'/%3E%3C/svg%3E"); background-repeat: no-repeat; background-position: right 14px center; }
.pp-error { margin: 6px 0 0; font-size: 14px; font-weight: 600; color: #c2410c; }

.pp-skills { min-width: 0; margin: 14px 0 0; padding: 0; border: 0; }
.pp-skills legend { padding: 0; }
.pp-hint { margin: 0 0 10px; font-size: 14px; line-height: 1.45; color: #6a6080; }
.pp-chips { display: flex; flex-wrap: wrap; gap: 8px; }
.pp-chip { display: inline-flex; align-items: center; gap: 6px; min-height: 40px; padding: 0 14px 0 11px; border-radius: 0; border: 1.5px solid rgba(43,34,64,.18); background: #fff; font-family: inherit; font-size: 14px; font-weight: 600; color: #1c1529; cursor: pointer; transition: background-color .15s ease, border-color .15s ease, color .15s ease; }
.pp-chip svg { flex-shrink: 0; color: #6a6080; }
.pp-chip:hover { border-color: #4e3a69; }
.pp-chip[aria-pressed="true"] { background: #2b2240; border-color: #2b2240; color: #fff; }
.pp-chip[aria-pressed="true"] svg { color: #F0B429; }
.pp-chip:focus-visible, .pp-add-btn:focus-visible, .pp-skill-x:focus-visible { outline: 3px solid #FF6535; outline-offset: 2px; }
.pp-chip:disabled, .pp-add-btn:disabled { opacity: .45; cursor: not-allowed; }
.pp-skill-add { display: flex; gap: 8px; margin-top: 10px; }
.pp-skill-add .pp-input { flex: 1; min-width: 0; min-height: 44px; border-radius: 0; }
.pp-add-btn { flex: 0 0 auto; min-height: 44px; padding: 0 18px; border: 0; border-radius: 0; background: #efe9f7; font-family: inherit; font-size: 14.5px; font-weight: 700; color: #2b2240; cursor: pointer; }
.pp-add-btn:not(:disabled):hover { background: #e4dbf1; }
.pp-skill-list { list-style: none; margin: 12px 0 0; padding: 0; display: grid; gap: 8px; }
/* phones: the name and × on top, the three levels under them; wider forms: one line */
.pp-skill-row { display: grid; grid-template-columns: minmax(0, 1fr) auto; grid-template-areas: "name x" "levels levels"; align-items: center; gap: 6px 10px; padding: 8px 8px 8px 14px; border-radius: 0; border: 1.5px solid rgba(43,34,64,.14); background: #fff; }
.pp-skill-row.is-bad { border-color: #d94b1e; background: #fff8f5; }
@container ppform (min-width: 420px) { .pp-skill-row { grid-template-columns: minmax(0, 1fr) auto auto; grid-template-areas: "name levels x"; } .pp-levels { width: 246px; } }
.pp-skill-name { grid-area: name; font-size: 15px; font-weight: 700; line-height: 1.3; color: #1c1529; overflow-wrap: anywhere; }
.pp-levels { grid-area: levels; display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 3px; padding: 3px; border-radius: 0; background: #f1edf7; }
.pp-level { position: relative; display: block; cursor: pointer; }
.pp-level input { position: absolute; inset: 0; width: 100%; height: 100%; margin: 0; opacity: 0; cursor: pointer; }
.pp-level span { display: grid; place-items: center; min-height: 36px; padding: 0 6px; border-radius: 0; font-size: 14px; font-weight: 700; color: #5c5270; transition: background-color .15s ease, color .15s ease; }
.pp-level:hover span { background: rgba(255,255,255,.75); color: #1c1529; }
.pp-level input:checked + span { background: #2b2240; color: #fff;  }
.pp-level input:focus-visible + span { outline: 3px solid #FF6535; outline-offset: 1px; }
.pp-skill-x { grid-area: x; display: grid; place-items: center; width: 36px; height: 36px; border: 0; border-radius: 0; background: transparent; color: #6a6080; cursor: pointer; }
.pp-skill-x:hover { background: rgba(43,34,64,.06); color: #1c1529; }
.pp-file { position: absolute; width: 1px; height: 1px; opacity: 0; pointer-events: none; }
.pp-drop { display: flex; align-items: center; gap: 14px; min-height: 64px; padding: 14px 16px; border-radius: 0; border: 2px dashed rgba(78,58,105,.35); background: #f7f4fb; color: #4e3a69; font-size: 15px; cursor: pointer; transition: border-color .15s ease, background-color .15s ease; }
.pp-drop:hover, .pp-drop.is-over { border-color: #4e3a69; background: #efe9f7; }
.pp-drop.is-bad { border-color: #d94b1e; }
.pp-file:focus-visible + .pp-drop { outline: 3px solid #FF6535; outline-offset: 2px; }
.pp-drop strong { color: #1c1529; }
.pp-drop-hint { display: block; margin-top: 2px; font-size: 14px; color: #6a6080; }
.pp-file-chosen { display: flex; align-items: center; gap: 12px; min-height: 64px; padding: 12px 12px 12px 14px; border-radius: 0; border: 1.5px solid rgba(30,122,79,.35); background: #effaf3; }
.pp-file-icon { flex: 0 0 auto; display: grid; place-items: center; width: 40px; height: 40px; border-radius: 0; background: #fff; color: #1e7a4f; }
.pp-file-name { display: block; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; font-size: 14.5px; font-weight: 700; color: #1c1529; }
.pp-file-size { display: block; font-size: 14px; color: #5c5270; }
.pp-file-x { flex: 0 0 auto; display: grid; place-items: center; width: 40px; height: 40px; border: 0; border-radius: 0; background: transparent; color: #5c5270; cursor: pointer; }
.pp-file-x:hover { background: rgba(43,34,64,.06); color: #1c1529; }
.pp-fail { margin: 16px 0 0; padding: 12px 14px; border-radius: 0; background: #fff1ec; color: #b23a10; font-size: 14px; font-weight: 600; line-height: 1.5; }
.pp-form .pp-btn--block { margin-top: 20px; min-height: 56px; font-size: 16.5px; }
.pp-consent { margin: 12px 0 0; font-size: 14px; line-height: 1.5; color: #6a6080; text-align: center; }
.pp-trap { position: absolute; left: -9999px; width: 1px; height: 1px; overflow: hidden; }
.pp-done { display: flex; flex-direction: column; align-items: center; text-align: center; padding-top: 40px; padding-bottom: 36px; }
.pp-done-icon { display: grid; place-items: center; width: 68px; height: 68px; border-radius: 50%; background: #1e7a4f; color: #fff; }



.pp-closing { background: var(--g-mist); }
@media (max-width: 759px) { .pp-page .pp-closing { padding-bottom: 112px !important; } }
.pp-reply { display: grid; grid-template-columns: minmax(0, 1fr); gap: 28px; padding: clamp(26px, 5vw, 80px); background: var(--paper); color: var(--ink); }
.pp-page .pp-reply h2 { max-width: 15em; font-size: clamp(2rem, .9rem + 4.6vw, 4.5rem); line-height: 1; }
.pp-closing-ctas { display: flex; flex-wrap: wrap; gap: 12px; margin-top: 30px; }
.pp-closing-ctas .pp-btn--ghost { background: var(--plum); color: #fff; }
.pp-closing-ctas .pp-btn--ghost:hover { filter: brightness(1.15); }
@media (max-width: 480px) { .pp-closing-ctas { flex-direction: column; } .pp-closing-ctas .pp-btn { width: 100%; } }
.pp-trust { list-style: none; margin: 28px 0 0; padding: 0; display: grid; gap: 8px; }
.pp-trust li { display: grid; grid-template-columns: 22px minmax(0, 1fr); gap: 10px; align-items: start; line-height: 1.3; }
.pp-trust svg { margin-top: 3px; color: var(--violet); }
.pp-reply-arrow svg { display: block; width: min(100%, 300px); margin-inline: auto; }
@media (min-width: 900px) { .pp-reply { grid-template-columns: minmax(0, 8fr) minmax(0, 4fr); align-items: center; gap: 56px; } .pp-reply-arrow svg { width: 100%; } }


.pp-cta-bar { position: fixed; left: 0; right: 0; bottom: 0; z-index: 50; display: grid; grid-template-columns: 1.6fr 1fr; gap: 6px; padding: 8px 8px calc(8px + env(safe-area-inset-bottom)); background: var(--night); transform: translateY(150%); visibility: hidden; transition: transform .3s ease, visibility 0s linear .3s; }
.pp-cta-bar.is-on { transform: none; visibility: visible; transition: transform .3s ease; }
.pp-cta-bar .pp-btn { min-height: 50px; padding: 0 14px; }
@media (min-width: 760px) { .pp-cta-bar { display: none; } }
@media (prefers-reduced-motion: reduce) { .pp-btn, .pp-btn svg, .pp-player-btn, .pp-video-poster img, .pp-video-play { transition: none; } .pp-btn:hover, .pp-btn:hover svg { transform: none; } }
`;

// ─── PAGE ───────────────────────────────────────────────────────────────────────
export default function PathToPlacement() {
  useSEO();
  const fees = useFees();

  return (
    <MotionConfig reducedMotion="user">
      <div className="pp-page">
        <style>{GLOBAL_STYLE}</style>
        <main>
          <Hero fees={fees} />
          <SoniaSection />
          <SixWeeks />
          <HowItWorks />
          <DontSection />
          <Pricing fees={fees} />
          <JoinSection fees={fees} />
          <Testimonials />
          <ClosingSection fees={fees} />
        </main>
        <MobileCtaBar />
      </div>
    </MotionConfig>
  );
}
